/**
 * Getting a photograph off the device.
 *
 * Two steps, in this order, and the order matters: the file goes to storage
 * FIRST, then the row is written. A row pointing at an object that does not
 * exist is a broken estimate; an object with no row is invisible waste that
 * the retention sweep clears. One of those is recoverable and the other is
 * what the customer sees.
 *
 * Paths are `<shop_id>/<job_id>/<photo_id>.jpg`, because the storage policies
 * key on the first segment. Building the path anywhere else would let the two
 * drift.
 */

import type { CapturedPhoto } from './photos.js';

export interface UploadTarget {
  shopId: string;
  jobId: string;
  slot: string;
}

export type UploadOutcome =
  | { kind: 'uploaded'; photoId: string; storagePath: string }
  | { kind: 'failed'; message: string; retryable: boolean };

export interface UploadDeps {
  /** Upload bytes. Resolves with an error message, or null on success. */
  putObject(path: string, blob: Blob, contentType: string): Promise<string | null>;
  /** Write the photos row. Resolves with an error message, or null. */
  insertRow(row: {
    id: string; shopId: string; jobId: string; slot: string;
    storagePath: string; width: number; height: number; takenAt: string;
  }): Promise<string | null>;
  newId(): string;
}

export function photoPath(t: UploadTarget, photoId: string): string {
  return `${t.shopId}/${t.jobId}/${photoId}.jpg`;
}

/**
 * A failure is retryable unless it is the shop's own fault.
 *
 * The distinction decides whether the offline queue keeps the shot or discards
 * it: a file too large will never succeed however many times it is tried, and
 * silently retrying it forever would look like a photo that never uploads.
 */
export function isRetryable(message: string): boolean {
  const m = message.toLowerCase();
  if (m.includes('exceeded the maximum allowed size')
      || m.includes('mime type')
      || m.includes('invalid')) return false;
  return true;
}

export async function uploadPhoto(
  photo: CapturedPhoto,
  blob: Blob,
  target: UploadTarget,
  deps: UploadDeps,
): Promise<UploadOutcome> {
  const photoId = deps.newId();
  const path = photoPath(target, photoId);

  const putError = await deps.putObject(path, blob, 'image/jpeg');
  if (putError) {
    return { kind: 'failed', message: putError, retryable: isRetryable(putError) };
  }

  const rowError = await deps.insertRow({
    id: photoId,
    shopId: target.shopId,
    jobId: target.jobId,
    slot: target.slot,
    storagePath: path,
    width: photo.width,
    height: photo.height,
    takenAt: photo.takenAt,
  });
  if (rowError) {
    // The object is already up. Retrying re-uploads it under a NEW id rather
    // than reusing this one, so a half-finished attempt never leaves a row
    // pointing at bytes from a different photograph.
    return { kind: 'failed', message: rowError, retryable: isRetryable(rowError) };
  }

  return { kind: 'uploaded', photoId, storagePath: path };
}
